import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isMfaChallenge,
  type AuthUser,
  type LoginResponse,
  type MfaChallenge,
} from '@mirsad/shared';
import { api } from '@/lib/api';

const CLE_SESSION = ['auth', 'me'] as const;

/**
 * Session courante.
 *
 * `retry: false` est délibéré : un 401 signifie « pas connecté », pas une
 * panne réseau. Réessayer ne ferait qu'ajouter du bruit et de la latence.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: CLE_SESSION,
    queryFn: async (): Promise<AuthUser | null> => {
      try {
        const { data } = await api.get<AuthUser>('/auth/me');
        return data;
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (identifiants: { email: string; password: string }) => {
      const { data } = await api.post<LoginResponse | MfaChallenge>('/auth/login', identifiants);
      return data;
    },
    onSuccess: (data) => {
      // Un défi MFA n'ouvre pas encore de session : ne pas peupler le cache.
      if (!isMfaChallenge(data)) qc.setQueryData(CLE_SESSION, data.user);
    },
  });
}

export function useVerifyMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { ticket: string; code: string }) => {
      const { data } = await api.post<LoginResponse | MfaChallenge>('/auth/mfa/verify', params);
      return data;
    },
    onSuccess: (data) => {
      if (!isMfaChallenge(data)) qc.setQueryData(CLE_SESSION, data.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout');
    },
    onSettled: () => {
      // Même si l'appel échoue, la session locale doit être oubliée.
      qc.setQueryData(CLE_SESSION, null);
      qc.clear();
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (params: { currentPassword: string; newPassword: string }) => {
      await api.post('/auth/change-password', params);
    },
  });
}

/** Marque et couleurs, servies avant authentification. */
export function useBrand() {
  return useQuery({
    queryKey: ['settings', 'brand'],
    queryFn: async () => {
      const { data } = await api.get<{
        nomOrganisation: string;
        logoUrl: string | null;
        couleurPrimaire?: string;
        couleurAccent?: string;
        couleurSurlignage?: string;
        couleurFond?: string;
      }>('/settings/brand');
      return data;
    },
    staleTime: 5 * 60_000,
    // Point d'accès public et léger : relu au retour sur l'onglet, pour qu'un
    // changement de marque atteigne aussi les sessions déjà ouvertes.
    refetchOnWindowFocus: true,
  });
}
